import Minze, { MinzeElement, EventListeners, Reactive, Attrs } from 'minze'
import { Locale } from '../../modules/locale'
import { Font } from '../../constants'
import { unescapeHtml } from '../../modules/utils'

export interface LvcDeleteProfilePopup {
  deleteConfirmationPrivacyPolicyMode: boolean
  deleteConfirmationTermsAndConditionsMode: boolean
  passwordProvider: string | boolean
  passwordInputValue: string
  deleteConfirmationPasswordError: string | boolean
}

export class LvcDeleteProfilePopup extends MinzeElement {
  attrs: Attrs = [
    ['delete-confirmation-privacy-policy-mode', false],
    ['delete-confirmation-terms-and-conditions-mode', false],
    ['password-provider', false],
  ]
  reactive: Reactive = [
    ['passwordInputValue', ''],
    ['deleteConfirmationPasswordError', false],
  ]

  closeDeleteAccountModal = () => {
    this.remove()
  }

  deleteAccount = async () => {
    const inputEl = this.select('#delete-account-password') as HTMLInputElement
    Minze.cast('lvc:delete_profile_confirmed', {
      password: unescapeHtml(inputEl?.value ?? ''),
      reason: this.deleteConfirmationPrivacyPolicyMode
        ? 'privacy_policy'
        : this.deleteConfirmationTermsAndConditionsMode
          ? 'terms_and_conditions'
          : 'none',
    })
  }

  openCookiesPreferences = () => {
    const button: HTMLButtonElement | undefined | null = document
      ?.querySelector('#axeptio_overlay')
      ?.querySelector('.needsclick')
      ?.shadowRoot?.querySelector('#axeptio_main_button')
    button?.click()
  }

  handleDeleteProfileError = (e: CustomEvent) => {
    if (!e.detail) return
    if (e.detail.error) {
      this.deleteConfirmationPasswordError = e.detail.error
    }
  }

  html = () => {
    return `
    <lvc-closeable-modal max-width="512px" no-close-button="true">
      <h3 class="delete-account-title">
        ${Locale.getKey(
          this.deleteConfirmationPrivacyPolicyMode
            ? 'profileSecurity.deleteAccountPrivacyPolicyMode.title'
            : this.deleteConfirmationTermsAndConditionsMode
              ? 'profileSecurity.deleteAccountTermsAndConditionsMode.title'
              : 'profileSecurity.deleteAccountConfirmationModalTitle'
        )}
      </h3>
      <p class="delete-account-text">
        ${Locale.getKey(
          this.deleteConfirmationPrivacyPolicyMode
            ? 'profileSecurity.deleteAccountPrivacyPolicyMode.text'
            : this.deleteConfirmationTermsAndConditionsMode
              ? 'profileSecurity.deleteAccountTermsAndConditionsMode.text'
              : 'profileSecurity.deleteAccountConfirmationModalText'
        )}
      </p>
      ${
        this.deleteConfirmationPrivacyPolicyMode
          ? `
        <lvc-box display="flex" flexDirection="column" columnGap="8px" rowGap="8px" mb="16px">
          <a
            class="deleteAccountLink"
            href="${Locale.getKey('profileSecurity.deleteAccountPrivacyPolicyMode.link1Url')}"
            target="_blank"
          >
            ${Locale.getKey('profileSecurity.deleteAccountPrivacyPolicyMode.link1Label')}
          </a>
          <a
            class="deleteAccountLink"
            href="#"
            id="change-preferences-link"
          >
            ${Locale.getKey('profileSecurity.deleteAccountPrivacyPolicyMode.link2Label')}
          </a>
        </lvc-box>
      `
          : this.deleteConfirmationTermsAndConditionsMode
            ? `
        <lvc-box mb="16px">
          <a
            class="deleteAccountLink"
            href="${Locale.getKey('profileSecurity.deleteAccountTermsAndConditionsMode.linkUrl')}"
            target="_blank"
           >
            ${Locale.getKey('profileSecurity.deleteAccountTermsAndConditionsMode.linkLabel')}
          </a>
        </lvc-box>
      `
            : ''
      }
      ${
        this.passwordProvider
          ? `
        <lvc-input 
          name="password" 
          type="password" 
          id="delete-account-password"
          border="${this.deleteConfirmationPasswordError ? 'unset' : '1px solid #1E007D'}"
          label="${Locale.getKey('profileSecurity.deleteAccountConfirmationModalInputLabel')}"
          error="${this.deleteConfirmationPasswordError}"
          placeholder="${Locale.getKey('profileSecurity.deleteAccountConfirmationModalInputPlaceholder')}"
        ></lvc-input>`
          : ``
      }
      <div class="delete-account-buttons">
        <lvc-button
          border="1px solid #1E007D"
          padding="14px 24px"
          theme="transparent"
          width="100%"
          class="button"
          id="delete-account-cancel-button"
          type="submit"
        >
          ${Locale.getKey('profileSecurity.deleteAccountConfirmationModalCancelButton')}
        </lvc-button>
        <lvc-button
          padding="14px 24px"
          bg="#FF4600"
          border="1px solid #FF4600"
          width="100%"
          class="button"
          id="delete-account-confirm-button"
          type="submit"
        >
          ${Locale.getKey('profileSecurity.deleteAccountConfirmationModalConfirmButton')}
        </lvc-button>
      </div>
    </lvc-closeable-modal>
    `
  }

  css = () => `
    .delete-account-title {
      font-weight: ${Font.weight.extraBold};
      font-size: 24px;
      line-height: 120%;
      color: #1E007D;
      text-transform: uppercase;
      margin: 0;
    }
    
    .delete-account-text {
      font-weight: ${Font.weight.medium};
      font-size: 17px;
      line-height: 24px;
      color: #1E007D;
      margin: 40px 0;
    }
    
    .delete-account-buttons {
      margin-top: 40px;
      display: flex;
      gap: 16px;
      justify-content: flex-end;
    }
    
    .deleteAccountLink {
      color: #8e88d5;
      font-size: 17px;
    }
  `

  eventListeners: EventListeners = [
    ['#delete-account-cancel-button', 'click', this.closeDeleteAccountModal],
    ['#delete-account-confirm-button', 'click', this.deleteAccount.bind(this)],
    ['#change-preferences-link', 'click', this.openCookiesPreferences.bind(this)],
    [window, 'lvc:delete_profile_error', e => this.handleDeleteProfileError(e as CustomEvent)],
  ]
}
